<script lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';

/**
 * Stacking tiers.
 *
 * `confirm` is not "one more modal". It is the dialog that interrupts a modal to
 * ask whether the thing about to happen is really wanted, so it has to outrank
 * every ordinary modal no matter who opened first.
 */
export type ModalTier = 'modal' | 'confirm';

export interface ModalStackEntry {
  token: number;
  tier: ModalTier;
}

/**
 * Band per tier, deliberately below ToastHost's 80: an error toast is how the app
 * reports that the very action being confirmed just failed, so it stays above
 * everything here. PostDetailDrawer sits at 50, under both bands.
 */
export const MODAL_TIER_BASE: Record<ModalTier, number> = { modal: 60, confirm: 70 };

/** "Who is on top" ranking, kept apart from the z-index arithmetic on purpose. */
const TIER_RANK: Record<ModalTier, number> = { modal: 0, confirm: 1 };

/**
 * How far one tier may climb before it would run into the band above it. Past
 * this point same-tier dialogs share a z-index: two peers fighting over paint
 * order is survivable, an ordinary modal outranking a confirm is the bug this
 * whole file exists to prevent. Ten deep is already far past anything real.
 */
const MAX_TIER_DEPTH = 9;

/**
 * The z-index for `token`, derived from open order rather than from DOM order.
 *
 * DOM order used to decide this, and it lied. Both dialogs teleport to <body>,
 * and Vue 3.5's Teleport appends its anchor to the target when the OWNING
 * component mounts — not in the order the dialogs are declared or opened.
 * ConfirmHost lives in the app shell, so its anchor lands at app boot; a modal
 * inside a lazily routed view mounts later and lands after it. Equal z-index
 * plus later-in-DOM meant the ordinary modal painted over the confirm dialog and
 * ate every click aimed at it, and the screen simply looked frozen.
 */
export function zIndexFor(entries: readonly ModalStackEntry[], token: number): number {
  const index = entries.findIndex((candidate) => candidate.token === token);
  const entry = entries[index];
  // A dialog that is not on the stack is not on screen; its value is unused.
  if (entry === undefined) return MODAL_TIER_BASE.modal;

  const depth = entries
    .slice(0, index)
    .filter((candidate) => candidate.tier === entry.tier).length;
  return MODAL_TIER_BASE[entry.tier] + Math.min(depth, MAX_TIER_DEPTH);
}

/**
 * The dialog a global key press belongs to: highest tier first, then most
 * recently opened.
 *
 * Every open modal listens on `window`, so before this guard one Escape closed
 * the confirm dialog AND the modal underneath it. Closing that modal was the
 * quiet half of the damage: it answered "no" to the question the user was still
 * reading, cancelling the approval they had just started.
 */
export function topmostToken(entries: readonly ModalStackEntry[]): number | null {
  let top: ModalStackEntry | null = null;
  for (const entry of entries) {
    if (top === null || TIER_RANK[entry.tier] >= TIER_RANK[top.tier]) top = entry;
  }
  return top === null ? null : top.token;
}

/**
 * Whether a finished press on the backdrop may close the dialog.
 *
 * A `click` fires on the NEAREST COMMON ANCESTOR of where the pointer went down
 * and where it came up. Start a text selection inside a form field, release a
 * few pixels past the dialog's edge, and that ancestor is the backdrop — so
 * `@click.self` treated an overshooting drag as a backdrop click and threw the
 * half-filled form away. Closing therefore demands the WHOLE gesture on the
 * backdrop: pointer down on it, pointer up on it, and the click's own target on
 * it. The click requirement is not redundant with the other two — a native
 * scrollbar drag (the backdrop scrolls tall dialogs) fires pointerdown/up on
 * the backdrop but never a click, and must not close anything.
 */
export function backdropPressCloses(press: {
  downOnBackdrop: boolean;
  upOnBackdrop: boolean;
  clickOnBackdrop: boolean;
  persistent: boolean;
}): boolean {
  return press.downOnBackdrop && press.upOnBackdrop && press.clickOnBackdrop && !press.persistent;
}

/**
 * One stack shared by every instance: module scope in an SFC is per-module, not
 * per-component.
 *
 * Reactive because closing a dialog has to renumber the ones left behind. A
 * survivor that kept the z-index of its old, deeper position would eventually be
 * matched by the next dialog to open, the two would tie, and DOM order — the
 * thing we stopped trusting — would decide again.
 */
const stackEntries = ref<ModalStackEntry[]>([]);
let nextToken = 1;
</script>

<script setup lang="ts">
/** A teleported dialog. Escape closes the topmost one; the backdrop closes unless `persistent`. */

const props = withDefaults(
  defineProps<{
    open: boolean;
    title: string;
    width?: string;
    persistent?: boolean;
    tier?: ModalTier;
  }>(),
  { width: '560px', persistent: false, tier: 'modal' },
);

const emit = defineEmits<{ (event: 'close'): void }>();

const token = ref<number | null>(null);

const zIndex = computed(() =>
  token.value === null ? MODAL_TIER_BASE[props.tier] : zIndexFor(stackEntries.value, token.value),
);

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  // Listeners from the dialogs underneath are still attached; they stay silent.
  if (token.value === null || topmostToken(stackEntries.value) !== token.value) return;
  emit('close');
}

// Where the current press began and ended; both must be the backdrop itself
// before its click may close the dialog. See backdropPressCloses().
let pressStartedOnBackdrop = false;
let pressEndedOnBackdrop = false;

function onBackdropPointerDown(event: PointerEvent): void {
  pressStartedOnBackdrop = event.target === event.currentTarget;
}

function onBackdropPointerUp(event: PointerEvent): void {
  pressEndedOnBackdrop = event.target === event.currentTarget;
}

function onBackdropClick(event: MouseEvent): void {
  const press = {
    downOnBackdrop: pressStartedOnBackdrop,
    upOnBackdrop: pressEndedOnBackdrop,
    clickOnBackdrop: event.target === event.currentTarget,
    persistent: props.persistent,
  };
  pressStartedOnBackdrop = false;
  pressEndedOnBackdrop = false;
  if (backdropPressCloses(press)) emit('close');
}

function enter(): void {
  if (token.value !== null) return;
  const assigned = nextToken++;
  token.value = assigned;
  stackEntries.value = [...stackEntries.value, { token: assigned, tier: props.tier }];
  window.addEventListener('keydown', onKeydown);
}

function leave(): void {
  const assigned = token.value;
  if (assigned === null) return;
  token.value = null;
  stackEntries.value = stackEntries.value.filter((entry) => entry.token !== assigned);
  window.removeEventListener('keydown', onKeydown);
}

watch(
  () => props.open,
  (open) => {
    if (open) enter();
    else leave();
  },
  { immediate: true },
);

// Routed views unmount while their modal is still open. Leaving the entry behind
// would push every later dialog one step deeper and misdirect Escape forever.
onBeforeUnmount(leave);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="backdrop"
      :style="{ zIndex }"
      @pointerdown="onBackdropPointerDown"
      @pointerup="onBackdropPointerUp"
      @click="onBackdropClick"
    >
      <div class="dialog card" role="dialog" aria-modal="true" :style="{ width }">
        <header class="dialog-head">
          <h2>{{ title }}</h2>
          <button class="close" type="button" aria-label="Đóng" @click="emit('close')">×</button>
        </header>
        <div class="dialog-body">
          <slot />
        </div>
        <footer v-if="$slots.footer" class="dialog-foot">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.backdrop {
  position: fixed;
  inset: 0;
  /* z-index is bound inline from the stack; see zIndexFor(). */
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 16px;
  overflow-y: auto;
  background: rgb(0 0 0 / 55%);
}

.dialog {
  max-width: 100%;
  box-shadow: var(--app-shadow);
}

.dialog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--app-border);
}

.dialog-head h2 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}

.close {
  border: none;
  background: transparent;
  color: var(--app-muted);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.dialog-body {
  padding: 18px;
}

.dialog-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 18px;
  border-top: 1px solid var(--app-border);
}
</style>
